import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const CategorySidebar = ({ 
  activeCategory, 
  activeSubItem, 
  onCategoryChange, 
  onSubItemChange 
}) => {
  const [expandedCategories, setExpandedCategories] = useState([activeCategory]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();

  const settingsCategories = [
    {
      id: 'organization',
      label: 'Organization',
      icon: 'Building2',
      badge: null,
      items: [
        { id: 'profile', label: 'Profile', icon: 'User' },
        { id: 'branding', label: 'Branding', icon: 'Palette' },
        { id: 'custom-domain', label: 'Custom Domain', icon: 'Globe' },
        { id: 'locations', label: 'Locations', icon: 'MapPin', badge: 'New' },
      ]
    },
    {
      id: 'users-roles',
      label: 'Users & Roles',
      icon: 'Users',
      badge: null,
      items: [
        { id: 'users', label: 'Users', icon: 'User' },
        { id: 'roles', label: 'Roles', icon: 'Shield' },
      ]
    },
    {
      id: 'user-preferences',
      label: 'User Preferences',
      icon: 'Settings',
      badge: null,
      items: [
        { id: 'preferences', label: 'Preferences', icon: 'Sliders' },
      ]
    },
    {
      id: 'taxes-compliance',
      label: 'Taxes & Compliance',
      icon: 'FileText',
      badge: null,
      items: [
        { id: 'taxes', label: 'Taxes', icon: 'Calculator' },
      ]
    },
    {
      id: 'setup-configurations',
      label: 'Setup & Configurations',
      icon: 'Cog',
      badge: null,
      items: [
        { id: 'general', label: 'General', icon: 'Settings' },
        { id: 'currencies', label: 'Currencies', icon: 'DollarSign' },
        { id: 'opening-balances', label: 'Opening Balances', icon: 'TrendingUp' },
        { id: 'reminders', label: 'Reminders', icon: 'Bell' },
        { id: 'customer-portal', label: 'Customer Portal', icon: 'Users' },
        { id: 'vendor-portal', label: 'Vendor Portal', icon: 'Truck' },
        { id: 'manual-transactions', label: 'Manual Transactions', icon: 'Plus', badge: 'New' },
        { id: 'statement-imports', label: 'Statement Imports', icon: 'Upload', badge: 'New' },
      ]
    },
    {
      id: 'customization',
      label: 'Customization',
      icon: 'Paintbrush',
      badge: null,
      items: [
        { id: 'transaction-number-series', label: 'Transaction Number Series', icon: 'Hash' },
        { id: 'pdf-templates', label: 'PDF Templates', icon: 'FileText' },
        { id: 'email-notifications', label: 'Email Notifications', icon: 'Mail' },
        { id: 'reporting-tags', label: 'Reporting Tags', icon: 'Tag' },
        { id: 'web-tabs', label: 'Web Tabs', icon: 'Layout' },
      ]
    },
    {
      id: 'automation',
      label: 'Automation',
      icon: 'Zap',
      badge: null,
      items: [
        { id: 'workflow-rules', label: 'Workflow Rules', icon: 'GitBranch' },
        { id: 'workflow-actions', label: 'Workflow Actions', icon: 'Play' },
        { id: 'workflow-logs', label: 'Workflow Logs', icon: 'Activity' },
      ]
    },
    {
      id: 'module-settings',
      label: 'Module Settings',
      icon: 'Package',
      badge: '15',
      items: [
        { id: 'general', label: 'General', icon: 'Settings' },
        { id: 'customers-vendors', label: 'Customers and Vendors', icon: 'Users' },
        { id: 'items', label: 'Items', icon: 'Package' },
        { id: 'accountant', label: 'Accountant', icon: 'Calculator' },
        { id: 'projects', label: 'Projects', icon: 'Folder' },
        { id: 'timesheet', label: 'Timesheet', icon: 'Clock' },
        { id: 'online-payments', label: 'Online Payments', icon: 'CreditCard' },
        { id: 'payment-gateways', label: 'Payment Gateways', icon: 'Wallet' },
        { id: 'sales', label: 'Sales', icon: 'TrendingUp' },
        { id: 'quotes', label: 'Quotes', icon: 'FileText' },
        { id: 'retainer-invoices', label: 'Retainer Invoices', icon: 'Receipt' },
        { id: 'delivery-challans', label: 'Delivery Challans', icon: 'Truck' },
        { id: 'invoices', label: 'Invoices', icon: 'FileText' },
        { id: 'recurring-invoices', label: 'Recurring Invoices', icon: 'RotateCcw' },
        { id: 'payments-received', label: 'Payments Received', icon: 'ArrowDown' },
        { id: 'credit-notes', label: 'Credit Notes', icon: 'FileText' },
        { id: 'delivery-notes', label: 'Delivery Notes', icon: 'Truck' },
        { id: 'packing-slips', label: 'Packing Slips', icon: 'Package' },
        { id: 'purchases', label: 'Purchases', icon: 'ShoppingCart' },
        { id: 'expenses', label: 'Expenses', icon: 'Receipt' },
        { id: 'bills', label: 'Bills', icon: 'FileText' },
        { id: 'payments-made', label: 'Payments Made', icon: 'ArrowUp' },
        { id: 'vendor-credits', label: 'Vendor Credits', icon: 'FileText' },
      ]
    },
    {
      id: 'extensions-developer',
      label: 'Extension and Developer Data',
      icon: 'Code',
      badge: null,
      items: [
        { id: 'integrations-marketplace', label: 'Integrations & Marketplace', icon: 'Grid3x3' },
        { id: 'zoho-apps', label: 'Zoho Apps', icon: 'Grid3x3' },
        { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' },
        { id: 'sms-integrations', label: 'SMS Integrations', icon: 'MessageSquare' },
        { id: 'other-apps', label: 'Other Apps', icon: 'Grid3x3' },
        { id: 'marketplace', label: 'Marketplace', icon: 'Store' },
        { id: 'developer-data', label: 'Developer Data', icon: 'Code' },
        { id: 'incoming-webhooks', label: 'Incoming Webhooks', icon: 'Webhook' },
        { id: 'connections', label: 'Connections', icon: 'Link' },
        { id: 'api-usage', label: 'API Usage', icon: 'Activity' },
      ]
    },
    {
      id: 'data-management',
      label: 'Data Management',
      icon: 'Database',
      badge: null,
      items: [
        { id: 'deluge-components-usage', label: 'Deluge Components Usage', icon: 'Code' },
        { id: 'web-forms', label: 'Web Forms', icon: 'FileText' },
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev?.includes(categoryId) 
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleItemClick = (categoryId, itemId) => {
    onCategoryChange(categoryId);
    onSubItemChange(itemId);
    
    // Update URL for bookmarkable navigation
    navigate(`/organization-settings/${categoryId}/${itemId}`, { replace: true });
    
    // Expand the category if not already expanded
    if (!expandedCategories?.includes(categoryId)) {
      setExpandedCategories(prev => [...prev, categoryId]);
    }
  };

  // Quick navigation for popular settings
  const handleQuickNavigation = (shortcut) => {
    const shortcuts = {
      'reminders': { category: 'setup-configurations', item: 'reminders' },
      'customer-portal': { category: 'setup-configurations', item: 'customer-portal' },
      'vendor-portal': { category: 'setup-configurations', item: 'vendor-portal' }
    };
    
    if (shortcuts?.[shortcut]) {
      const { category, item } = shortcuts?.[shortcut];
      handleItemClick(category, item);
    }
  };

  const collapseAll = () => {
    setExpandedCategories([]);
  };

  const expandAll = () => {
    setExpandedCategories(settingsCategories?.map(cat => cat?.id));
  };

  // Filter categories and items based on search term
  const filteredCategories = settingsCategories?.map(category => {
    if (!searchTerm) return category;
    
    const categoryMatch = category?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const filteredItems = category?.items?.filter(item => 
      item?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
    
    if (categoryMatch || filteredItems?.length > 0) {
      return {
        ...category,
        items: filteredItems?.length > 0 ? filteredItems : category?.items
      };
    }
    return null;
  })?.filter(Boolean);

  // Auto-expand categories with search matches
  React.useEffect(() => {
    if (searchTerm) {
      const matchingCategories = filteredCategories?.map(cat => cat?.id);
      setExpandedCategories(matchingCategories);
    }
  }, [searchTerm]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  const getTotalItems = () => {
    return settingsCategories?.reduce((total, category) => total + category?.items?.length, 0);
  };

  const getVisibleItems = () => {
    return filteredCategories?.reduce((total, category) => total + category?.items?.length, 0);
  };

  return (
    <div className="w-80 bg-surface border-r border-border flex flex-col h-full">
      {/* Search Header */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search settings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-9 pr-8 py-2 text-sm bg-background border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded"
            >
              <Icon name="X" size={14} className="text-muted-foreground" />
            </button>
          )}
        </div>
        
        {/* Quick Access Buttons */}
        <div className="mt-3 grid grid-cols-1 gap-1">
          <button
            onClick={() => handleQuickNavigation('reminders')}
            className="flex items-center space-x-2 px-2 py-1.5 text-xs bg-muted/50 hover:bg-primary/10 hover:text-primary rounded transition-colors"
          >
            <Icon name="Bell" size={12} />
            <span>Quick: Reminders</span>
          </button>
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => handleQuickNavigation('customer-portal')}
              className="flex items-center justify-center space-x-1 px-2 py-1.5 text-xs bg-muted/50 hover:bg-primary/10 hover:text-primary rounded transition-colors"
            >
              <Icon name="Users" size={12} />
              <span>Customers</span>
            </button>
            <button
              onClick={() => handleQuickNavigation('vendor-portal')}
              className="flex items-center justify-center space-x-1 px-2 py-1.5 text-xs bg-muted/50 hover:bg-primary/10 hover:text-primary rounded transition-colors"
            >
              <Icon name="Truck" size={12} />
              <span>Vendors</span>
            </button>
          </div>
        </div>
        
        {/* Search Results Counter */}
        {searchTerm && (
          <div className="mt-2 text-xs text-muted-foreground">
            {getVisibleItems()} of {getTotalItems()} settings found
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="px-4 py-2 border-b border-border">
        <div className="flex space-x-2">
          <button
            onClick={expandAll}
            className="flex-1 px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded text-center transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="flex-1 px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded text-center transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Categories List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-2">
          {filteredCategories?.length > 0 ? (
            filteredCategories?.map((category) => (
              <div key={category?.id} className="space-y-1">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category?.id)}
                  onMouseEnter={() => setHoveredItem(`category-${category?.id}`)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                    expandedCategories?.includes(category?.id) 
                      ? "bg-primary/10 text-primary" :"text-muted-foreground hover:text-foreground hover:bg-muted/50",
                    hoveredItem === `category-${category?.id}` && "shadow-sm"
                  )}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={category?.icon} size={16} />
                    <span>{category?.label}</span>
                    {category?.badge && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                        {category?.badge}
                      </span>
                    )}
                  </div>
                  <Icon 
                    name={expandedCategories?.includes(category?.id) ? "ChevronDown" : "ChevronRight"} 
                    size={16}
                    className={cn(
                      "transition-transform duration-200",
                      expandedCategories?.includes(category?.id) && "transform rotate-0"
                    )}
                  />
                </button>

                {/* Category Items */}
                {expandedCategories?.includes(category?.id) && (
                  <div className="ml-6 space-y-1 animate-in slide-in-from-top-2 duration-200">
                    {category?.items?.map((item) => (
                      <button
                        key={item?.id}
                        onClick={() => handleItemClick(category?.id, item?.id)}
                        onMouseEnter={() => setHoveredItem(`item-${category?.id}-${item?.id}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className={cn(
                          "w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                          activeCategory === category?.id && activeSubItem === item?.id
                            ? "bg-primary/10 text-primary font-medium shadow-sm" 
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                          hoveredItem === `item-${category?.id}-${item?.id}` && "shadow-sm scale-[1.02]"
                        )}
                      >
                        <Icon name={item?.icon} size={14} />
                        <span className="flex-1 text-left">{item?.label}</span>
                        {item?.badge && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                            {item?.badge}
                          </span>
                        )}
                        {activeCategory === category?.id && activeSubItem === item?.id && (
                          <Icon name="Dot" size={12} className="text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="Search" size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No settings found</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try a different search term
              </p>
              <button
                onClick={clearSearch}
                className="mt-3 px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded transition-colors"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        <div className="text-xs text-muted-foreground text-center">
          {settingsCategories?.length} categories • {getTotalItems()} settings
        </div>
      </div>
    </div>
  );
};

export default CategorySidebar;